import React from "react";
import { Avatar, IconButton, Tooltip } from "@mui/material";
import { isImpersonatingAccount, isImpersonatingUser } from "shared-ui/src/utils/clientmanagement";
import CxEmployeeAvatar, { AvatarSize } from "@components/CxEmployeeAvatar";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";

interface UserAvatarProps {
  firstName: string;
  lastName: string;
  emailAddress: string;
  isShiUser: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  menuOpen?: boolean;
}

export default function UserAvatar({
  firstName,
  lastName,
  emailAddress,
  isShiUser,
  onClick,
  menuOpen = false,
}: UserAvatarProps) {
  const { getClaimValue, getClaimValueAsBoolean } = useClaims();

  const isImpersonating = getClaimValueAsBoolean("isImpersonating");

  const userEntraId =
    isShiUser &&
    !isImpersonatingAccount() &&
    !isImpersonatingUser() &&
    !isImpersonating
      ? getClaimValue("userEntraId")
      : null;

  const displayName =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    emailAddress?.split("@")[0] ||
    "Unknown User";

  if (userEntraId) {
    return (
      <Tooltip title="User Menu" enterDelay={500}>
        <IconButton
          id="user-avatar-button"
          aria-label="User Menu"
          aria-controls={menuOpen ? "unity-courtesy-header-user-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen}
          {...telemetryProps({ name: "header_user_avatar", section: "unified_header", label: "User Menu" })}
          onClick={onClick}
        >
          <CxEmployeeAvatar
            entraId={userEntraId}
            displayName={displayName}
            photoSize={AvatarSize.medium}
          />
        </IconButton>
      </Tooltip>
    );
  }

  const initials =
    firstName.length && lastName.length
      ? `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`
      : emailAddress.charAt(0).toUpperCase() || "NA";

  return (
    <Tooltip title="User Menu" enterDelay={500}>
      <IconButton
      id="user-avatar-button"
      aria-label="User Menu"
      aria-controls={menuOpen ? "unity-courtesy-header-user-menu" : undefined}
      aria-haspopup="true"
      aria-expanded={menuOpen}
      {...telemetryProps({ name: "header_user_avatar", section: "unified_header", label: "User Menu" })}
      onClick={onClick}
    >
      <Avatar
        component={"span"}
        sx={[
          () => ({
            border: "1px solid",
            width: "40px",
            height: "40px",
          }),
          (theme) =>
            theme.applyStyles("dark", {
              color: theme.palette.text.primary,
              bgcolor: theme.palette.grey[800],
              borderColor: theme.palette.grey[600],
            }),
          (theme) =>
            theme.applyStyles("light", {
              color: theme.palette.text.secondary,
              bgcolor: theme.palette.common.white,
              borderColor: theme.palette.table.border,
            }),
        ]}
      >
        {initials}
      </Avatar>
    </IconButton>
    </Tooltip>
  );
}
